import { cn } from "../../lib/cn";
import type { BadgeTone } from "./Badge";

export interface ProgressMeterProps {
  value: number;
  max: number;
  tone?: Extract<BadgeTone, "accent" | "success" | "warn" | "danger">;
  label?: string;
  className?: string;
}

const toneBarClasses: Record<NonNullable<ProgressMeterProps["tone"]>, string> = {
  accent: "bg-accent",
  success: "bg-success",
  warn: "bg-warn",
  danger: "bg-danger",
};

/** Used for daily/monthly quota bars, campaign progress, and battery-style meters. */
export function ProgressMeter({ value, max, tone = "accent", label, className }: ProgressMeterProps) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {label && <p className="text-sm text-text-muted">{label}</p>}
      <div
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        className="h-2.5 w-full overflow-hidden rounded-pill bg-surface-hover/70 ring-1 ring-inset ring-border/50"
      >
        <div
          className={cn(
            "h-full rounded-pill animate-meter-fill",
            // Value changes after mount (the SSE feed updates quota live) still need to move.
            "transition-[width] duration-slow ease-standard motion-reduce:transition-none",
            toneBarClasses[tone],
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
